/* ============================================================
   MOONRISE — Product tabs custom element
   Reference: moon-rise-framer/script.js lines 808-826

   Behavior:
     - WAI-ARIA tablist with arrow-key navigation
     - Activates the data-default-tab tab on connect (or the first tab)
     - Reads URL hash on load: #panel-<section>-<block_id> opens that tab
     - Updates the URL hash on activation (history.replaceState)
     - Respects prefers-reduced-motion (panel fade animation already disabled
       via CSS, this element does nothing extra in that mode)
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('product-tabs')) return;

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length || !this.panels.length) return;

      this._byTabId = {};
      this._byPanelId = {};
      var self = this;
      this.tabs.forEach(function (t) {
        var key = t.getAttribute('data-tab');
        if (key) self._byTabId[key] = t;
      });
      this.panels.forEach(function (p) {
        var key = p.getAttribute('data-panel');
        if (key) self._byPanelId[key] = p;
      });

      this._bindClicks();
      this._bindKeyboard();
      this._bindHashChange();

      var initial = this._resolveInitial();
      this.activate(initial, { focus: false, updateHash: false });
    }

    _resolveInitial() {
      // 1. Hash takes precedence
      var fromHash = this._readHash();
      if (fromHash && this._byTabId[fromHash]) return fromHash;
      // 2. data-default-tab on the host element
      var def = this.getAttribute('data-default-tab');
      if (def && this._byTabId[def]) return def;
      // 3. First tab
      return this.tabs[0].getAttribute('data-tab');
    }

    _readHash() {
      if (!window.location.hash) return null;
      var h = window.location.hash.replace(/^#/, '');
      // Format: panel-<section>-<block_id>
      var panel = document.getElementById(h);
      if (panel && this.contains(panel)) {
        return panel.getAttribute('data-panel');
      }
      // Or short form: tab-<block_id>
      if (this._byTabId[h]) return h;
      return null;
    }

    _bindClicks() {
      var self = this;
      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          self.activate(tab.getAttribute('data-tab'), { focus: true, updateHash: true });
        });
      });
    }

    _bindKeyboard() {
      var self = this;
      this.tabs.forEach(function (tab, index) {
        tab.addEventListener('keydown', function (e) {
          var i = index;
          var handled = false;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { i = (index + 1) % self.tabs.length; handled = true; }
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { i = (index - 1 + self.tabs.length) % self.tabs.length; handled = true; }
          else if (e.key === 'Home') { i = 0; handled = true; }
          else if (e.key === 'End') { i = self.tabs.length - 1; handled = true; }
          if (handled) {
            e.preventDefault();
            self.activate(self.tabs[i].getAttribute('data-tab'), { focus: true, updateHash: true });
          }
        });
      });
    }

    _bindHashChange() {
      var self = this;
      window.addEventListener('hashchange', function () {
        var key = self._readHash();
        if (key) self.activate(key, { focus: false, updateHash: false });
      });
    }

    activate(key, opts) {
      opts = opts || {};
      if (!key || !this._byTabId[key]) return;

      var activeTab = this._byTabId[key];
      var activePanel = this._byPanelId[key];

      this.tabs.forEach(function (t) {
        var isActive = t === activeTab;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      this.panels.forEach(function (p) {
        var isActive = p === activePanel;
        p.classList.toggle('is-active', isActive);
        if (isActive) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });

      if (opts.focus && activeTab) {
        try { activeTab.focus({ preventScroll: true }); } catch (e) { activeTab.focus(); }
      }
      if (opts.updateHash && activePanel && window.history && window.history.replaceState) {
        var newHash = '#' + activePanel.id;
        if (window.location.hash !== newHash) {
          window.history.replaceState({}, '', newHash);
        }
      }
    }
  }

  customElements.define('product-tabs', ProductTabs);
})();
